import {AfterViewInit, Component, inject, Input, OnDestroy} from '@angular/core';

import {MarkantElementsModule} from '@markant/elements-angular';
import {take} from 'rxjs/operators';
import {environment} from '@environments/environment';
import {FreshdeskSupportService} from '@services/freshdesk-support.service';
import {VoilaUserService} from '@services/voila-user.service';

declare global {
  interface Window {
    fwSettings: {
      locale: string;
      widget_id: number;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    FreshworksWidget: any;
  }
}

@Component({
  selector: 'freshdesk-support',
  templateUrl: './freshdesk-support.component.html',
  standalone: true,
  imports: [MarkantElementsModule],
})
export class FreshdeskSupportComponent implements AfterViewInit, OnDestroy {
  @Input()
  registrationVariant: boolean = false;

  private readonly freshdeskSupportService = inject(FreshdeskSupportService);
  private readonly userService = inject(VoilaUserService);

  private get widgetId(): string {
    return this.registrationVariant ? environment.freshdeskRegistrationWidgetId : environment.freshdeskWidgetId;
  }

  private get scriptSrc(): string {
    return `https://euc-widget.freshworks.com/widgets/${this.widgetId}.js`;
  }

  private get widgetIdNumber(): number {
    return Number.parseInt(this.widgetId, 10);
  }

  private script!: HTMLScriptElement;
  private htmlElement!: HTMLElement | null;

  ngAfterViewInit(): void {
    this.loadFreshDeskWidget();
  }

  ngOnDestroy(): void {
    this.removeAndDestroyWidget();
  }

  private removeAndDestroyWidget() {
    if (this.script && this.htmlElement) {
      const widgetContainer = document.getElementById('freshworks-container');
      widgetContainer?.remove();
      window.FreshworksWidget('destroy');
    }
  }

  private loadFreshDeskWidget(): void {
    window.fwSettings = {
      widget_id: this.widgetIdNumber,
      locale: 'de',
    };

    // A Widget-Loader-Stub to handle method calls before the external widget script is loaded (avoiding race
    // conditions) This code ist taken from the DVA project
    // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/no-unused-expressions
    !(function (): any {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const n = function (): any {
        // eslint-disable-next-line prefer-rest-params
        n.q.push(arguments);
      };
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      (n.q = []), (window.FreshworksWidget = n);
    })();

    this.script = document.createElement('script');
    this.script.type = 'text/javascript';
    this.script.src = this.scriptSrc;

    this.htmlElement = document.getElementById('freshdeskWidget');

    if (this.htmlElement) {
      this.htmlElement.appendChild(this.script);
      this.userService
        .loggedInUser()
        .pipe(take(1))
        .subscribe(() => {
          if (!this.registrationVariant) {
            this.freshdeskSupportService.getTokenForSignedInUser().subscribe((token) => {
              window.FreshworksWidget('authenticate', {token: token});
            });
          }
        });
    }

    window.FreshworksWidget('setLabels', {
      de: {
        contact_form: {
          title: 'Ihre Anfrage',
          submit: 'Senden',
          confirmation: 'Vielen Dank für Ihre Anfrage',
        },
      },
    });

    if (!this.registrationVariant) {
      window.FreshworksWidget('prefill', 'ticketForm', {
        custom_fields: {
          cf_categorie: 'eService',
          cf_subcategory: 'Voilà!',
        },
      });
    }
  }
}
